import React, { Suspense, lazy, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import ErrorPage from './error-page';
import ErrorBoundary from './components/ErrorBoundary';
import { PageLoader } from './components/LoadingStates';
import { prefetchAllRoutes } from './utils/routePrefetch';

// Import Home directly - it's the landing page and always needed first
import Home from './components/pages/Home';

// Lazy load other page components for code splitting
const Language = lazy(() => import('./components/pages/Language'));
const DictionaryPage = lazy(() => import('./components/pages/Dictionary'));
const Curriculum = lazy(() => import('./components/pages/Curriculum'));
const Resources = lazy(() => import('./components/pages/Resources'));
const Contact = lazy(() => import('./components/pages/Contact'));
const CommunityBlog = lazy(() => import('./components/Community'));

// Suppress specific console errors in production
if (process.env.NODE_ENV === 'production') {
  const originalError = console.error;
  console.error = (...args) => {
    // Suppress audio 416 errors and curriculum 404 errors
    const message = args.join(' ');
    if ((message.includes('416') && message.includes('audio')) ||
        (message.includes('404') && message.includes('curriculum'))) {
      return; // Don't log these specific errors
    }
    originalError.apply(console, args);
  };
}

// Wrapper component for lazy-loaded routes
const LazyRoute = ({ children }) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

// Home doesn't need lazy wrapper since it's imported directly
const HomeRoute = () => (
  <ErrorBoundary>
    <Home />
  </ErrorBoundary>
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        errorElement: <ErrorPage />,
        children: [
          { index: true, element: <HomeRoute /> },
          {
            path: "/home",
            element: <HomeRoute />,
            children: [
              {
                path: "#new",
                element: <LazyRoute><CommunityBlog /></LazyRoute>
              },
            ],
          },
          {
            path: "/language",
            element: <LazyRoute><Language /></LazyRoute>
          },
          {
            path: "/dictionary",
            element: <LazyRoute><DictionaryPage /></LazyRoute>
          },
          {
            path: "/curriculum",
            element: <LazyRoute><Curriculum /></LazyRoute>
          },
          {
            path: "/resources",
            element: <LazyRoute><Resources /></LazyRoute>
          },
          {
            path: "/contact",
            element: <LazyRoute><Contact /></LazyRoute>
          },
        ],
      },
    ],
  },

]);

// Prefetch App Wrapper - triggers route prefetching after initial load
const PrefetchingApp = () => {
  useEffect(() => {
    // Prefetch all routes after the app loads and becomes idle
    prefetchAllRoutes();
  }, []);

  return <RouterProvider router={router} />;
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  // <React.StrictMode>
    <ChakraProvider>
        <ColorModeScript initialColorMode="light"></ColorModeScript>
        <PrefetchingApp />
    </ChakraProvider>
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
